import {
  DurationPickerV2,
  InfoIconPopover,
  InfoPopover,
  Typography,
} from '@automata/ui';
import { TimeBreakdownStrings } from '@automata/ui/src/DurationPickerV2/types';
import { Box, FormControlLabel, Stack, Switch } from '@mui/material';
import { ChangeEvent, FC } from 'react';

import {
  TimeConstraintHardData,
  TimeConstraintModalData,
} from '../../TimeConstraintModal';

const DETAILED_COVERAGE_TOOLTIP = (
  <div>
    <p>Detailed coverage controls how strictly this constraint is enforced:</p>
    <ul>
      <li>
        <b>Off:</b> the system checks the constraint only at key task boundaries
        (e.g., when the next task starts).
      </li>
      <li>
        <b>On:</b> the system enforces the constraint across the entire
        interval, detecting violations caused by runtime delays, pauses, or
        scheduling interruptions.
      </li>
    </ul>
  </div>
);

const AUTO_IGNORE_TOOLTIP = (
  <div>
    <p>
      Controls whether an alert is shown if the constraint is broken at runtime.
      The planner continues applying the constraint until it is deemed broken,
      regardless of this setting.
    </p>
    <ul>
      <li>
        <b>On:</b> No alert is shown.
      </li>
      <li>
        <b>Off:</b> An alert is shown.
      </li>
    </ul>
  </div>
);

export type Props = {
  minBreakdown: TimeBreakdownStrings;
  maxBreakdown: TimeBreakdownStrings;
  onMinUpdate: (patch: Partial<TimeBreakdownStrings>) => void;
  onMaxUpdate: (patch: Partial<TimeBreakdownStrings>) => void;
  onMinBlur: () => void;
  onMaxBlur: () => void;
  formData: TimeConstraintModalData;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onAutoIgnoreToggle: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const TimeConstraintParameters: FC<Props> = ({
  minBreakdown,
  maxBreakdown,
  onMinUpdate,
  onMaxUpdate,
  onMinBlur,
  onMaxBlur,
  formData,
  handleChange,
  onAutoIgnoreToggle,
}: Props) => {
  return (
    <>
      <Stack spacing={1}>
        <Stack direction="row" spacing={2}>
          <Stack flex={1}>
            <DurationPickerV2
              label="MINIMUM TIME"
              hours={minBreakdown.hours}
              minutes={minBreakdown.minutes}
              seconds={minBreakdown.seconds}
              handleBlurParent={onMinBlur}
              handleUpdateTime={onMinUpdate}
            />
          </Stack>
          <Stack flex={1}>
            <DurationPickerV2
              label="MAXIMUM TIME"
              hours={maxBreakdown.hours}
              minutes={maxBreakdown.minutes}
              seconds={maxBreakdown.seconds}
              handleBlurParent={onMaxBlur}
              handleUpdateTime={onMaxUpdate}
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack>
        <Box display="flex" flexDirection="row" gap={1} alignItems="center">
          <Typography variant="overline">Detailed Coverage</Typography>
          <InfoIconPopover
            infoPopover={
              <InfoPopover
                title="Detailed coverage"
                copy={DETAILED_COVERAGE_TOOLTIP}
              />
            }
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              id="canvas-parameterReference-switch"
              checked={
                (formData as TimeConstraintHardData)
                  .enableDetailedCoverageCalculation
              }
              onChange={handleChange}
            />
          }
          label={
            (formData as TimeConstraintHardData)
              .enableDetailedCoverageCalculation
              ? 'On'
              : 'Off'
          }
        />
      </Stack>

      <Stack>
        <Box display="flex" flexDirection="row" gap={1} alignItems="center">
          <Typography variant="overline">Auto Ignore</Typography>
          <InfoIconPopover
            infoPopover={
              <InfoPopover title="Auto Ignore" copy={AUTO_IGNORE_TOOLTIP} />
            }
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={(formData as TimeConstraintHardData).autoIgnoreIfBroken}
              onChange={onAutoIgnoreToggle}
            />
          }
          label={
            (formData as TimeConstraintHardData).autoIgnoreIfBroken
              ? 'On'
              : 'Off'
          }
        />
      </Stack>
    </>
  );
};
